<template>
  <v-dialog
    v-model="isOpen"
    :fullscreen="$vuetify.breakpoint.smAndDown"
    max-width="760px"
    scrollable>
    <v-card v-if="account">
      <v-card-title class="d-flex align-center">
        <v-avatar v-if="launcherLogo" size="24" class="mr-2" tile>
          <v-img :src="launcherLogo" contain />
        </v-avatar>
        <div style="min-width: 0">
          <div class="text-truncate">{{ account.username }}</div>
          <div class="caption text--secondary">
            {{ $t('sharedAccounts.history.title') }}
          </div>
        </div>
        <v-spacer />
        <v-btn icon @click="close"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>
      <v-divider />

      <v-card-text class="pa-0" style="height: 56vh">
        <div
          v-if="loading"
          class="d-flex justify-center align-center fill-height">
          <v-progress-circular indeterminate color="primary" />
        </div>

        <template v-else-if="sessions.length">
          <!-- Desktop: one row per session, expand for its borrows -->
          <v-simple-table v-if="$vuetify.breakpoint.smAndUp">
            <template v-slot:default>
              <thead>
                <tr>
                  <th style="width: 40px"></th>
                  <th class="text-left">
                    {{ $t('sharedAccounts.history.when') }}
                  </th>
                  <th class="text-left">
                    {{ $t('sharedAccounts.filters.launcher') }}
                  </th>
                  <th class="text-left">
                    {{ $t('sharedAccounts.attributes.games') }}
                  </th>
                  <th class="text-left">
                    {{ $t('sharedAccounts.history.session') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <template v-for="s in sessions">
                  <tr
                    :key="s.session_id"
                    class="event-row"
                    @click="toggleExpand(s.session_id)">
                    <td class="px-2">
                      <v-icon small>
                        {{
                          expanded[s.session_id]
                            ? 'mdi-chevron-up'
                            : 'mdi-chevron-down'
                        }}
                      </v-icon>
                    </td>
                    <td class="text-no-wrap">
                      {{ fullDate(s.first_borrowed_at) }}
                    </td>
                    <td>
                      <div class="d-flex align-center">
                        <v-avatar
                          v-if="s.app && s.app.icon"
                          size="18"
                          class="mr-2"
                          tile>
                          <v-img :src="s.app.icon" contain />
                        </v-avatar>
                        <span>{{ s.app ? s.app.name : '—' }}</span>
                      </div>
                    </td>
                    <td>
                      {{ gamesSummary(s)
                      }}<span
                        v-if="borrowCount(s) > 1"
                        class="text--secondary caption ml-1">
                        ×{{ borrowCount(s) }}
                      </span>
                    </td>
                    <td @click.stop>
                      <v-skeleton-loader
                        v-if="enriching"
                        type="text"
                        width="120" />
                      <span
                        v-else-if="sessionLabel(s)"
                        class="text-truncate d-inline-block"
                        style="max-width: 160px"
                        :title="sessionLabel(s)">
                        {{ sessionLabel(s) }}
                      </span>
                      <span
                        v-else
                        class="text--secondary"
                        :title="s.session_id">
                        {{ $t('sharedAccounts.otherArena') }}
                      </span>
                    </td>
                  </tr>
                  <tr
                    v-if="expanded[s.session_id]"
                    :key="s.session_id + '-d'"
                    class="detail-row">
                    <td></td>
                    <td colspan="4" class="py-2">
                      <div
                        v-for="(log, j) in sessionLogs(s)"
                        :key="j"
                        class="caption log-line py-1">
                        <v-icon
                          x-small
                          :color="
                            log.type === 'borrow' ? 'primary' : 'success'
                          ">
                          {{
                            log.type === 'borrow'
                              ? 'mdi-login-variant'
                              : 'mdi-logout-variant'
                          }}
                        </v-icon>
                        <span
                          class="font-weight-medium ml-1"
                          :class="
                            log.type === 'borrow'
                              ? 'primary--text'
                              : 'success--text'
                          ">
                          {{
                            log.type === 'borrow'
                              ? $t('sharedAccounts.history.borrowedAt')
                              : $t('sharedAccounts.history.returnedAt')
                          }}
                        </span>
                        · {{ fullDate(log.at)
                        }}<template v-if="log.game">
                          · {{ log.game.name }}</template
                        >
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </template>
          </v-simple-table>

          <!-- Mobile: cards -->
          <div v-else class="pa-2">
            <v-card
              v-for="s in sessions"
              :key="s.session_id"
              outlined
              class="mb-2 pa-3">
              <div class="d-flex align-center">
                <v-avatar
                  v-if="s.app && s.app.icon"
                  size="18"
                  class="mr-2"
                  tile>
                  <v-img :src="s.app.icon" contain />
                </v-avatar>
                <span class="font-weight-medium text-truncate">
                  {{ gamesSummary(s) }}
                </span>
              </div>
              <div class="caption text--secondary mt-1">
                {{ fullDate(s.first_borrowed_at) }}
              </div>
              <div class="caption mt-1 d-flex align-center">
                <span class="text--secondary mr-1">
                  {{ $t('sharedAccounts.history.session') }}:
                </span>
                <v-skeleton-loader v-if="enriching" type="text" width="120" />
                <span v-else-if="sessionLabel(s)">{{ sessionLabel(s) }}</span>
                <span v-else class="text--secondary">
                  {{ $t('sharedAccounts.otherArena') }}
                </span>
              </div>

              <template v-if="expanded[s.session_id]">
                <v-divider class="my-2" />
                <div
                  v-for="(log, j) in sessionLogs(s)"
                  :key="j"
                  class="caption py-1">
                  <v-icon
                    x-small
                    :color="log.type === 'borrow' ? 'primary' : 'success'">
                    {{
                      log.type === 'borrow'
                        ? 'mdi-login-variant'
                        : 'mdi-logout-variant'
                    }}
                  </v-icon>
                  <span
                    class="font-weight-medium ml-1"
                    :class="
                      log.type === 'borrow' ? 'primary--text' : 'success--text'
                    ">
                    {{
                      log.type === 'borrow'
                        ? $t('sharedAccounts.history.borrowedAt')
                        : $t('sharedAccounts.history.returnedAt')
                    }}
                  </span>
                  · {{ fullDate(log.at)
                  }}<template v-if="log.game"> · {{ log.game.name }}</template>
                </div>
              </template>

              <div class="text-center">
                <v-btn x-small text @click="toggleExpand(s.session_id)">
                  {{
                    expanded[s.session_id]
                      ? $t('sharedAccounts.history.hideDetails')
                      : $t('sharedAccounts.history.details')
                  }}
                  <v-icon x-small right>
                    {{
                      expanded[s.session_id]
                        ? 'mdi-chevron-up'
                        : 'mdi-chevron-down'
                    }}
                  </v-icon>
                </v-btn>
              </div>
            </v-card>
          </div>
        </template>

        <div v-else class="d-flex justify-center align-center fill-height">
          <span class="text--secondary">
            {{ $t('sharedAccounts.history.empty') }}
          </span>
        </div>
      </v-card-text>

      <v-divider />
      <DataTableFooter :loading="loading" :pagination="pagination" />
    </v-card>
  </v-dialog>
</template>

<script>
import DataTableFooter from '@/components/crud/DataTableFooter.vue'
import SessionsApi from '@/services/main/SessionsApi'
import SharedAccountsApi from '@/services/configuration/SharedAccountsApi'
import formatDate from '@/mixins/format-date'
import { mapActions } from 'vuex'

const resetPagination = () => ({
  current_page: 1,
  per_page: 10,
  total: 0,
  last_page: 0,
  from: 1,
  to: 0,
})

// Stop the Session-name shimmer after this if the lookup is slow/hangs.
const ENRICH_TIMEOUT_MS = 8000

export default {
  name: 'BorrowHistoryDialog',
  components: { DataTableFooter },
  mixins: [formatDate],
  props: {
    value: { type: Boolean, default: false },
    account: { type: Object, default: null },
    launcherLogo: { type: String, default: '' },
    arenaId: { type: [Number, String], default: null },
  },
  data() {
    return {
      loading: false,
      sessions: [],
      pagination: resetPagination(),
      // session_id -> { pc, guest } (best-effort enrichment)
      sessionInfo: {},
      // true while PC/guest names are being looked up (Session column shimmer)
      enriching: false,
      enrichTimer: null,
      // monotonic id so stale fetch/enrich responses can be ignored
      reqId: 0,
      // session index -> expanded (its borrows)
      expanded: {},
    }
  },
  computed: {
    isOpen: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      },
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.pagination = resetPagination()
        this.sessions = []
        this.sessionInfo = {}
        this.expanded = {}
        this.fetchHistory()
      }
    },
    'pagination.current_page'() {
      if (this.value) this.fetchHistory()
    },
  },
  beforeDestroy() {
    clearTimeout(this.enrichTimer)
  },
  methods: {
    ...mapActions(['showSnackbar']),
    async fetchHistory() {
      if (!this.arenaId || !this.account || !this.account.id) return
      const reqId = ++this.reqId
      this.loading = true
      this.expanded = {}
      try {
        const params = `page=${this.pagination.current_page}&size=${this.pagination.per_page}`
        const response = await SharedAccountsApi.borrowHistory(
          this.arenaId,
          this.account.id,
          params,
        )
        if (reqId !== this.reqId) return // superseded by a newer fetch
        const data = response.data || {}
        this.sessions = Array.isArray(data.data) ? data.data : []
        this.applyMetadata(data.metadata)
        this.enrichSessions(reqId)
      } catch (e) {
        if (reqId !== this.reqId) return
        console.error(e)
        this.sessions = []
        this.applyMetadata()
        this.showSnackbar({
          color: 'error',
          message: this.$t('sharedAccounts.errors.loadFailed'),
        })
      } finally {
        if (reqId === this.reqId) this.loading = false
      }
    },
    async enrichSessions(reqId) {
      const ids = [
        ...new Set(this.sessions.map((s) => s.session_id).filter(Boolean)),
      ]
      if (!ids.length) return
      this.sessionInfo = {}
      this.enriching = true
      clearTimeout(this.enrichTimer)
      // Don't shimmer forever if the lookup is slow/hangs - fall back gracefully.
      this.enrichTimer = setTimeout(() => {
        if (reqId === this.reqId) this.enriching = false
      }, ENRICH_TIMEOUT_MS)
      try {
        // NOTE: param name/delimiter pending arena confirmation - change here if needed.
        const response = await SessionsApi.getByArena(
          this.arenaId,
          `session_ids=${ids.join(',')}`,
        )
        if (reqId !== this.reqId) return // a newer fetch is in charge now
        const sessions = response.data?.data || []
        const map = {}
        for (const s of sessions) {
          if (!s || s.id == null) continue
          map[String(s.id)] = {
            pc: (s.device && s.device.hostname) || '',
            guest: (s.user && s.user.username) || '',
          }
        }
        this.sessionInfo = map
      } catch (e) {
        // Enrichment is optional; unresolved sessions render as "Other arena".
        if (reqId === this.reqId) this.sessionInfo = {}
      } finally {
        clearTimeout(this.enrichTimer)
        if (reqId === this.reqId) this.enriching = false
      }
    },
    applyMetadata(metadata) {
      if (!metadata) {
        this.pagination.total = 0
        this.pagination.last_page = 0
        this.pagination.from = 0
        this.pagination.to = 0
        return
      }
      const { count = 0, page = 1, size = this.pagination.per_page } = metadata
      this.pagination.total = count
      this.pagination.last_page = Math.max(1, Math.ceil(count / size))
      this.pagination.from = count === 0 ? 0 : (page - 1) * size + 1
      this.pagination.to = Math.min(page * size, count)
    },
    // ---- per-session helpers ----
    gamesSummary(s) {
      const names = [
        ...new Set(
          (s.borrows || []).map((b) => b.game && b.game.name).filter(Boolean),
        ),
      ]
      return names.join(', ') || '—'
    },
    borrowCount(s) {
      return (s.borrows || []).length
    },
    // Raw logs (borrow + return events) merged chronologically - no pairing.
    sessionLogs(s) {
      const logs = []
      for (const b of s.borrows || []) {
        logs.push({ type: 'borrow', at: b.borrowed_at, game: b.game })
      }
      for (const r of s.returns || []) {
        logs.push({ type: 'return', at: r, game: null })
      }
      const ts = (x) => {
        const d = new Date(x.at).getTime()
        return isNaN(d) ? 0 : d
      }
      // Newest first; malformed timestamps sort last.
      return logs.sort((a, b) => ts(b) - ts(a))
    },
    fullDate(iso) {
      return iso ? this.formatDate(new Date(iso), true) : '—'
    },
    sessionLabel(s) {
      const info = this.sessionInfo[String(s.session_id)]
      if (!info) return ''
      if (info.pc && info.guest) return `${info.pc} · ${info.guest}`
      return info.pc || info.guest || ''
    },
    toggleExpand(id) {
      this.$set(this.expanded, id, !this.expanded[id])
    },
    close() {
      this.isOpen = false
    },
  },
}
</script>

<style scoped>
.event-row {
  cursor: pointer;
}
.detail-row td {
  background-color: rgba(0, 0, 0, 0.02);
  border-bottom: thin solid rgba(0, 0, 0, 0.12);
}
.log-line + .log-line {
  border-top: thin solid rgba(0, 0, 0, 0.06);
}
/* Compact, inline skeleton for the Session cell while names load. */
::v-deep .v-skeleton-loader__text {
  margin-bottom: 0;
}
</style>
